<script lang="ts" setup>
import { onMounted, useTemplateRef } from 'vue';

import type { MonthsSelectorMonth } from './models';

interface MonthsSelectorInnerViewProps {
  months: MonthsSelectorMonth[];
  selectedMonthIsoDate: string;
}
const props = defineProps<MonthsSelectorInnerViewProps>();

interface MonthsSelectorInnerViewEmits {
  (eventName: 'confirm', newMonthSelection: Date): void;
}

const emit = defineEmits<MonthsSelectorInnerViewEmits>();

const wrapperRef = useTemplateRef('wrapperRef');

function onMonthSelect(month: MonthsSelectorMonth): void {
  if (!month.isExtended) {
    emit('confirm', new Date(month.firstDayInMonthISO));
  }
}

function scrollToSelectedMonth(): void {
  if (wrapperRef.value) {
    const selectedMonthEl = wrapperRef.value.querySelector(
      '.month.selected'
    ) as HTMLElement;
    if (selectedMonthEl) {
      const wrapper = wrapperRef.value;

      const wrapperWidth = wrapper.clientWidth;
      const selectedMonthWidth = selectedMonthEl.clientWidth;

      const selectedMonthOffsetLeft = selectedMonthEl.offsetLeft;

      const scrollTo =
        selectedMonthOffsetLeft - wrapperWidth / 2 + selectedMonthWidth / 2;

      wrapper.scrollTo({
        left: scrollTo,
        behavior: 'smooth',
      });
    }
  }
}

onMounted(() => {
  scrollToSelectedMonth();
});
</script>

<template>
  <div ref="wrapperRef" class="months-selector-inner-view">
    <div
      v-for="month of props.months"
      :key="month.firstDayInMonthISO"
      :class="['month', { selected: month.isExtended }]"
      @click.stop="onMonthSelect(month)"
    >
      <div class="month-description">
        <span class="name">
          {{ month.monthShort }}
        </span>
        <span class="year">
          {{ month.year }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.months-selector-inner-view {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  gap: var(--spacing-4);
  padding-bottom: var(--spacing-16);

  .month {
    flex: 0 0 auto;
    background-color: var(--color-white);
    border: 1px solid var(--color-neutral-30);
    border-radius: var(--border-radius-12);
    height: var(--tile-height);
    .month-description {
      padding: var(--spacing-24);
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);
      align-items: center;
      justify-content: center;
      color: var(--color-neutral-70);
      cursor: pointer;
      border-radius: var(--border-radius-12);
      transition: all var(--transition-short);
      @include on-hover {
        background-color: var(--color-neutral-30);
      }
      .name {
        @include font-label-l-bold;
      }
      .year {
        @include font-label-s-light;
      }
    }
    &.selected {
      .month-description {
        background-color: var(--color-neutral-70);
        color: var(--color-white);
        cursor: default;
      }
    }
  }
}
</style>
